/**
 * DeviceDetector.ts - Device detection and fingerprinting
 * 
 * CHANGE HISTORY:
 * - 251027_1747 danielbeer: Minimal implementation for basic device detection
 */

import type { DeviceContext } from '../types';

export class DeviceDetector {
  /**
   * Detect current device context
   */
  detect(): DeviceContext {
    const isBrowser = typeof window !== 'undefined';
    const isNode = typeof process !== 'undefined' && process.versions?.node;

    return {
      id: this.generateFingerprint(),
      type: this.detectDeviceType(),
      os: this.detectOS(),
      osVersion: this.detectOSVersion(),
      runtime: isNode ? 'node' : (isBrowser ? this.detectBrowser() : 'unknown'),
      runtimeVersion: isNode ? process.versions.node : (isBrowser ? this.detectBrowserVersion() : 'unknown'),
      screen: this.detectScreen(),
      capabilities: this.detectCapabilities(),
      firstSeen: new Date(),
      lastSeen: new Date(),
      trustLevel: 'new',
      isActive: true
    };
  }

  private generateFingerprint(): string {
    // Simple fingerprint for now
    const components = [
      typeof navigator !== 'undefined' ? navigator.userAgent : '',
      typeof screen !== 'undefined' ? `${screen.width}x${screen.height}` : '',
      new Date().getTimezoneOffset().toString()
    ];
    
    return btoa(components.join('|')).substring(0, 32);
  }

  private detectDeviceType(): 'desktop' | 'mobile' | 'tablet' | 'server' | 'unknown' {
    if (typeof navigator === 'undefined') return 'server';
    
    const ua = navigator.userAgent.toLowerCase();
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) return 'tablet';
    if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
    return 'desktop';
  }

  private detectOS(): string {
    if (typeof navigator === 'undefined') {
      return process?.platform || 'unknown';
    }

    const ua = navigator.userAgent;
    if (ua.includes('Win')) return 'Windows';
    if (ua.includes('Mac')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    return 'unknown';
  }

  private detectOSVersion(): string {
    if (typeof navigator === 'undefined') return 'unknown';
    
    const ua = navigator.userAgent;
    const match = ua.match(/(Windows NT|Mac OS X|Android|iOS) ([\d._]+)/);
    return match ? match[2] : 'unknown';
  }

  private detectBrowser(): string {
    if (typeof navigator === 'undefined') return 'unknown';
    
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Edge')) return 'Edge';
    return 'unknown';
  }

  private detectBrowserVersion(): string {
    if (typeof navigator === 'undefined') return 'unknown';
    
    const ua = navigator.userAgent;
    const match = ua.match(/(Chrome|Safari|Firefox|Edge)\/([\d.]+)/);
    return match ? match[2] : 'unknown';
  }

  private detectScreen() {
    if (typeof screen === 'undefined') {
      return { width: 0, height: 0, pixelRatio: 1 };
    }

    return {
      width: screen.width,
      height: screen.height,
      pixelRatio: window.devicePixelRatio || 1
    };
  }

  private detectCapabilities() {
    const hasNavigator = typeof navigator !== 'undefined';
    
    return {
      touch: hasNavigator && ('ontouchstart' in window),
      camera: hasNavigator && ('mediaDevices' in navigator),
      microphone: hasNavigator && ('mediaDevices' in navigator),
      geolocation: hasNavigator && ('geolocation' in navigator),
      notifications: hasNavigator && ('Notification' in window)
    };
  }
}
